import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import {
  getFeedIdentityGeneration,
  mergeConfirmedFeedPost,
  scheduleFeedReconciliation,
} from "@bw/shared/hooks";
import type { PostCreate, PostResponse } from "@bw/shared/types/generated";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/**
 * Share-a-personal-post mutation (S1d, WRITE). Posts `createPersonalPost({
 * content, media_urls })` — a PERSONAL post (no community); the server forces
 * community_id to null. On success it merges the confirmed server response into
 * eligible Feed caches and schedules bounded projection reconciliation.
 *
 * NON-OPTIMISTIC, mirroring useCreatePost / useCreateRepost: the cache is NOT
 * mutated before the server confirms — no client-side prepend of a phantom post,
 * no local count bump. A rejected create (403 post_permission / 422 / network)
 * leaves the feed exactly as the server last reported it and surfaces on
 * `mutation.error` for the composer to show honestly (and to retain the typed
 * content for a retry).
 *
 * Re-entrancy + the content/media gate are the composer's concern: it disables
 * Submit while `isPending` (so a double-click fires once) and blocks an
 * empty-content + no-media submit. This hook sends whatever PostCreate it is
 * handed.
 */
export function useCreatePersonalPost() {
  const queryClient = useQueryClient();

  return useMutation<PostResponse, unknown, PostCreate, number>({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (data: PostCreate) => communityApi.createPersonalPost(data),
    onSuccess: (post, _data, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      const targets = [
        { scope: "home" as const },
        { scope: "friends" as const },
        { scope: "user" as const, subject: post.author_id },
      ];
      mergeConfirmedFeedPost(queryClient, post, targets);
      scheduleFeedReconciliation(queryClient, targets);
    },
  });
}
